import type { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import i18n from '../../i18n';
import { SAGA_ACTIONS } from '../../sagas/sagaActions';
import { submitEduProof } from '../../shared/requestSagas';
import { setLoading } from '../../store/cartSlice';
import {
  clearError,
  EDU_STATUS,
  type EduForm,
  setEduStatus,
  setError,
  setForm,
  setSubmitted,
} from '../../store/eduSlice';
import type { RootState } from '../../store/store';
import type { Country } from '../../types/country';
import { validateEduProofFile, validateEduTextField } from './validators';

const TEXT_FIELDS = [
  'expectedGraduationDate',
  'institutionName',
  'institutionCity',
  'institutionCountry',
] as const;

function isTextField(
  field: keyof EduForm,
): field is (typeof TEXT_FIELDS)[number] {
  return (TEXT_FIELDS as readonly string[]).includes(field);
}

function* validateEduFormField(action: {
  payload: Partial<EduForm>;
}): SagaIterator<void> {
  const [field, value] = Object.entries(action.payload)[0] as [
    keyof EduForm,
    EduForm[keyof EduForm],
  ];

  let message: string | null = null;
  if (field === 'imageProof') {
    message = validateEduProofFile(value as EduForm['imageProof']);
  } else if (isTextField(field)) {
    message = validateEduTextField(value as string);
  }

  if (message == null) {
    yield put(clearError(field));
  } else {
    yield put(setError({ [field]: i18n.t(message) }));
  }
}

export function* watchEduForm(): SagaIterator<void> {
  yield takeLatest(setForm, validateEduFormField);
}

export function* watchSubmitProofOfStatus(): SagaIterator<void> {
  yield takeLatest(SAGA_ACTIONS.SUBMIT_PROOF_OF_STATUS, handleSubmit);
}

function* handleSubmit(): SagaIterator<void> {
  const { form } = (yield select((state: RootState) => state.eduStatus)) as {
    form: EduForm;
  };
  const countries = (yield select((state: RootState) => state.app.countries)) as
    | Country[]
    | null;

  const { imageProof, expectedGraduationDate, institutionCountry, ...rest } =
    form;

  const formData = new FormData();

  if (imageProof) {
    const fileResponse = (yield call(fetch, imageProof.url)) as Response;
    const blob = (yield call([fileResponse, fileResponse.blob])) as Blob;
    formData.append('imageProof', blob, imageProof.name);
  }

  formData.append('expectedGraduationDate', expectedGraduationDate);
  formData.append(
    'institutionCountry',
    countries?.find((country) => country.code === institutionCountry)?.name ??
      institutionCountry,
  );
  for (const [key, value] of Object.entries(rest)) {
    if (value) {
      formData.append(key, value);
    }
  }

  yield put(setLoading(true));
  try {
    yield call(submitEduProof, formData);
    yield put(setEduStatus(EDU_STATUS.PENDING));
    yield put(setSubmitted(true));
  } catch {
    yield put(
      setError({
        imageProof: i18n.t('Failed to submit proof of status.'),
      }),
    );
  } finally {
    yield put(setLoading(false));
  }
}
